import { jobBoardAPI } from './base';

const feedbackEndpoints = {
  submit: '/feedback',
  list: '/feedback'
};

export type AppFeedbackStatus = 'active' | 'resolved';
export type AppFeedbackStatusFilter = AppFeedbackStatus | 'all';

export interface SubmitAppFeedbackPayload {
  message: string;
  image?: File | null;
  route?: string;
  pageUrl?: string;
  source?: string;
  submittedByName?: string;
  submittedByEmail?: string;
  userAgent?: string;
}

export interface AppFeedbackResponse {
  id: string;
  message: string;
  status?: AppFeedbackStatus | null;
  route?: string | null;
  pageUrl?: string | null;
  source?: string | null;
  submittedByName?: string | null;
  submittedByEmail?: string | null;
  userAgent?: string | null;
  imageUrl?: string | null;
  sharePointFileName?: string | null;
  sharePointFileUrl?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface GetSubmittedFeedbackParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: AppFeedbackStatusFilter;
}

export interface PaginatedFeedbackResponse {
  page: number;
  limit: number;
  total: number;
  data: AppFeedbackResponse[];
}

export interface UpdateFeedbackStatusPayload {
  status: AppFeedbackStatus;
}

const unwrapFeedbackPayload = (response: { data?: any }) =>
  response?.data?.data || response?.data;

const normalizeFeedbackListResponse = (
  payload: unknown
): PaginatedFeedbackResponse => {
  if (Array.isArray(payload)) {
    return {
      page: 1,
      limit: payload.length,
      total: payload.length,
      data: payload
    };
  }

  const responsePayload = (payload || {}) as Partial<PaginatedFeedbackResponse>;
  const feedbackItems = Array.isArray(responsePayload?.data)
    ? responsePayload.data
    : [];

  return {
    page:
      typeof responsePayload?.page === 'number' && responsePayload.page > 0
        ? responsePayload.page
        : 1,
    limit:
      typeof responsePayload?.limit === 'number' && responsePayload.limit > 0
        ? responsePayload.limit
        : feedbackItems.length,
    total:
      typeof responsePayload?.total === 'number' && responsePayload.total >= 0
        ? responsePayload.total
        : feedbackItems.length,
    data: feedbackItems
  };
};

export const submitAppFeedback = async (
  payload: SubmitAppFeedbackPayload
): Promise<AppFeedbackResponse> => {
  const formData = new FormData();

  formData.append('message', payload.message.trim());

  if (payload.image) {
    formData.append('image', payload.image);
  }

  if (payload.route) {
    formData.append('route', payload.route);
  }

  if (payload.pageUrl) {
    formData.append('pageUrl', payload.pageUrl);
  }

  if (payload.source) {
    formData.append('source', payload.source);
  }

  if (payload.submittedByName) {
    formData.append('submittedByName', payload.submittedByName);
  }

  if (payload.submittedByEmail) {
    formData.append('submittedByEmail', payload.submittedByEmail);
  }

  if (payload.userAgent) {
    formData.append('userAgent', payload.userAgent);
  }

  const response = await jobBoardAPI.post<{ data: AppFeedbackResponse }>(
    feedbackEndpoints.submit,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return unwrapFeedbackPayload(response) as AppFeedbackResponse;
};

export const getSubmittedFeedback = async (
  params?: GetSubmittedFeedbackParams
): Promise<PaginatedFeedbackResponse> => {
  const normalizedParams = {
    ...params,
    status: params?.status === 'all' ? undefined : params?.status
  };

  const response = await jobBoardAPI.get(feedbackEndpoints.list, {
    params: normalizedParams
  });

  return normalizeFeedbackListResponse(unwrapFeedbackPayload(response));
};

export const updateFeedbackStatus = async (
  feedbackId: string,
  payload: UpdateFeedbackStatusPayload
): Promise<AppFeedbackResponse> => {
  const response = await jobBoardAPI.patch(
    `${feedbackEndpoints.list}/${feedbackId}/status`,
    payload
  );

  return unwrapFeedbackPayload(response) as AppFeedbackResponse;
};
